import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import helpers from "../../Utils/helpers";
import { hasAccess, hasAdminAccess } from "../../Utils/permissionUtils";
import Modals from "../../Constants/modals";
import routes from "../../Constants/routes";
import { UserRoles, Access } from '../../Constants/userRoles';
import useModal from "../../Hooks/useModal";
import UserLinks from "../NavigationLinks/UserLinks";
import TopNavigationBarItem from "../TopNavigationBarItem";
import TopNavigationSearch from "../Search/TopNavigationSearch";
import TopNavigationModalManager from "../ModalManagers/TopNavigationModalManager";

function TopNavigation({ openAppointmentsCount, openLeadsCount, user, t }) {
    const navigate = useNavigate();
    const { openModal } = useModal();
    
    const leftNav = useRef(null);
    const rightNav = useRef(null);
    const thresholdsRef = useRef(null);
    const leftThresholdRef = useRef(null);
    const rightThresholdRef = useRef(null);

    const hamburgerNavWidth = 370;
    const mobileThreshold = 590;
    const extraWidth = 120;
    const initialLinkIndex = 10;

    const [linkIndex, setLinkIndex] = useState(initialLinkIndex);
    const [mobileView, setMobileView] = useState(false);

    function handleResize() {
        if (rightThresholdRef.current && leftThresholdRef.current && thresholdsRef.current) {
            const innerWidth = window.innerWidth;

            let linkIndex = thresholdsRef.current.findIndex(element => innerWidth <= (element + hamburgerNavWidth));
            setLinkIndex(linkIndex);

            setMobileView(innerWidth <= mobileThreshold);
        }
    }

    useEffect(() => {
        setThresholds();

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [])

    function setThresholds() {
        setTimeout(() => {
            setLinkIndex(initialLinkIndex);
            setMobileView(false);

            let thresholds = [];

            let brandheader = document.getElementById("navbar-header-id");
            let current = brandheader.getBoundingClientRect().width + extraWidth;

            if (leftNav.current) {
                let links = leftNav.current.getElementsByClassName('text-nowrap');
                for (let i = 0; i < links.length; i++) {
                    current += links[i].getBoundingClientRect().width
                    thresholds.push(current)
                    leftThresholdRef.current = current;
                }
                thresholds.push(Number.MAX_SAFE_INTEGER);
                thresholdsRef.current = thresholds
            }

            if (rightNav.current) {
                let rightNavRect = rightNav.current.getBoundingClientRect().width + extraWidth;
                rightThresholdRef.current = rightNavRect;
            }
            handleResize();
        }, 500);
    }

    function getLinks() {
        let items = [
            {
                title: t("topnavigation.appointments"),
                path: routes.myAppointments,
                badgeNumber: openAppointmentsCount
            },
            {
                title: t("topnavigation.supportLogin"),
                path:"#",
                onClick: () => openModal(Modals.SupportLogin)
            },
            {
                title: t("topnavigation.leads"),
                path: routes.leads,
                badgeNumber: openLeadsCount,
                disabled: !hasAccess(user, UserRoles.Spcs, Access.ReadWrite)
            },
            // { title: t("topnavigation.quotation"), path: routes.quotation },
            { title: t("topnavigation.internalleads"), path: routes.internalLeads },
            { title: t("topnavigation.sendmail"), dropdownItems: getSendMailLinks() },
            { title: t("topnavigation.customerdirectory"), path: routes.customerDirectoryContainer, disabled: !hasAccess(user, UserRoles.AOSales, Access.ReadWrite) },
        ];
        return items.filter((link) => !link.disabled);
    }

    function getSendMailLinks() {
        let items = [
            {
                title: t("topnavigation.senddemo"),
                link: window.location.pathname,
                modalName: "showSendDemoEmailModal",
                onClick: () => openModal(Modals.SendDemoEmail)
            },
            {
                title: t("topnavigation.senddesktopversion"),
                link: window.location.pathname,
                modalName: "showSendDesktopVersionModal",
                onClick: () => openModal(Modals.SendDesktopVersion)
            },
            {
                title: t("topnavigation.noncustomermail"),
                link: window.location.pathname,
                modalName: "showNonCustomerEmailModal",
                onClick: () => openModal(Modals.NonCustomerEmail)
            },
            {
                title: t("topnavigation.sendmovie"),
                link: window.location.pathname,
                modalName: "showSendMovieModal",
                onClick: () => openModal(Modals.SendMovie)
            },
        ];
        return items;
    }

    function getIconLinks() {
        let items = [
            {
                title: t("topnavigation.alert"),
                dropdownItems: getNotifications().map((item, index) => {
                    item.classList = item.read ? "" : "bold"
                    item.path = item.link
                    item.onClick = () => onHandleNotification(index, item)
                    return item;
                }),
                badgeNumber: notificationsCount(),
                icon: "vismaicon-alert"
            },
            {
                title: t("topnavigation.usersearch"),
                icon: "vismaicon-users",
                path: window.location.pathname,
                modalName: "showUserSearchModal",
                onClick: () => openModal(Modals.UserSearch)
            },
            {
                title: t("topnavigation.productoverview"),
                icon: "vismaicon-library",
                path: routes.productOverview,
            },
            {
                title: t("topnavigation.status"),
                icon: "vismaicon-info",
                path: routes.status
            }
        ]

        if (Object.values(UserRoles).some(role => hasAccess(user, role, Access.Admin))) {
            items.push({
                title: t("topnavigation.administrate"),
                icon: "vismaicon-settings",
                path: routes.settings
            });
        }

        return items.filter(item => !item.disabled);
    }

    function getBurgerMenu() {
        let items = getBurgerLinks();

        let itemsWithBadgeNumber = items.filter((item) => item.badgeNumber && item.badgeNumber > 0);
        let badgeNumber = helpers.getArrayPropertyTotals(itemsWithBadgeNumber, "badgeNumber");

        let burgerMenu = [{
            icon: "vismaicon-burger",
            dropdownItems: items,
            badgeNumber: badgeNumber,
            className: "skynet-w-35"
        }]
        return burgerMenu;
    }

    function getBurgerLinks() {
        let links = getLinks()
            .filter((link, index) => index >= linkIndex);
        let iconLinks = getIconLinks();

        return links.concat(iconLinks);
    }

    function onHandleNotification(index, notification) {
        updateReadNotification(notification.id);
        navigate(notification.link);
    }

    const updateReadNotification = (id) => {
        let notifications = getNotifications();
        let index = notifications.findIndex(i => i.id === id);

        if (index !== -1) {
            notifications[index].read = true;
            saveNotifications(notifications);
        }
    }

    const getNotifications = () => {
        return localStorage.Notifications ? JSON.parse(localStorage.Notifications) : [];
    }

    const notificationsCount = () => {
        return getNotifications().filter(element => element.read === false).length;
    }

    const saveNotifications = (notifications) => {
        localStorage.Notifications = JSON.stringify(notifications);
    }

    return (
        <header className="navbar navbar-default d-flex" id="vudNavTopMain" role="navigation">
            <div className="navbar-header" id="navbar-header-id">
                <div className="navbar-brand">
                    <Link to='/'>Skynet</Link>
                </div>
            </div>
            {user && (
                <nav className="collapse navbar-collapse d-flex skynet-topnav-search" aria-label="Menu">
                    <ul className="nav navbar-nav navbar-left nav-tabs first-level d-flex" role="menubar" ref={leftNav}>
                        {
                            getLinks()
                                .filter((link, index) => index < linkIndex)
                                .map((item, index) => {
                                    return (<TopNavigationBarItem key={index} item={item} isMainDropDown={!!item.dropdownItems} />);
                                })}

                    </ul>
                    {<ul className="nav navbar-nav nav-tabs navbar-right first-level context-selector d-flex" ref={rightNav}>
                        <TopNavigationSearch />
                        {getBurgerMenu().map((item, index) => {
                            return (<TopNavigationBarItem key={index} item={item} isTopNav={true} isMainDropDown={true} />
                            )
                        })}
                        {user && <UserLinks location={window.location} showIcon={mobileView} />}
                    </ul>}
                </nav>
            )}

            <TopNavigationModalManager></TopNavigationModalManager>
        </header>
    );
}

function mapStateToProps(state) {
    return {
        user: state.CommonReducer.user,
        openAppointmentsCount: state.AppointmentReducer.openAppointmentsCount,
        openLeadsCount: state.LeadsReducer.openLeadsCount,
    };
}

function mapDispatchToProps() {
    return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(TopNavigation));
